import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams} from 'react-router-dom';
import { AiOutlineShopping, AiOutlineArrowLeft } from 'react-icons/ai';
import { GoHome } from 'react-icons/go';
import { client } from '../sanity/sanityClient';
import { useStateContext } from '../context/StateContext';
import Cart from './Cart';
import NavBar from './NavBar';
import FetchCategory from './FetchCategory';
import ProductDetail from './ProductDetail';
import ProductDesktop from './ProductDesktop';
import SlugProducts from './SlugProducts';
import './Products.css';
import 'react-toastify/dist/ReactToastify.css';

const Products = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [slugCategory, setSlugCategory] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [showCategory, setShowCategory] = useState(true);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const { totalQuantities, showCart, setShowCart } = useStateContext();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const PRODUCTS_PER_PAGE = 40;
  const [isMobile, setIsMobile] = useState(
  window.matchMedia("(max-width: 768px)").matches
);

useEffect(() => {
  const mediaQuery = window.matchMedia("(max-width: 768px)");

  const handleChange = (e) => {
    setIsMobile(e.matches);
  };

  mediaQuery.addEventListener("change", handleChange);

  return () => {
    mediaQuery.removeEventListener("change", handleChange);
  };
}, []);
  

  // Get currency from localStorage or default to 'TZS'
  const getCurrency = () => localStorage.getItem('selectedCurrency') || 'TZS';

  // Wrap fetchProducts in useCallback to avoid infinite re-renders
const fetchProducts = useCallback(async (page = 1, append = false) => {
  try {
    setLoading(true);
    const currency = getCurrency();
    const start = (page - 1) * PRODUCTS_PER_PAGE;
    const end = start + PRODUCTS_PER_PAGE;
    
    // CORRECTED: Use start and end variables in the range
    const data = await client.fetch(
        `*[_type in [
          "grocery", "beauty", "hotels", "estate", "clothingandfashion", "truck2cargo", "servicesandinstitutions",
          "kitchen", "crops", "electronics", "jewelryandaccessories", "eventsandnightclubs",
          "salon", "foodandbeverages", "sports", "arts", "medical", "agritech", "giftoptions", 
          "vehicles", "livestock", "hardware", "useditems", "construction","cuisines", "booksandstationery",
          "kidsandtoys", "hospitality", "homeandfurnitures", "fashion", "energyandfuel",
        ] && slug.current == $slug && currency == $currency][$start...$end]`,
        { slug, currency, start, end }
      );
    
    if (append) {
      setProducts(prev => [...prev, ...data]);
    } else {
      setProducts(data);
    }
    
    // Check if there are more products
    if (data.length < PRODUCTS_PER_PAGE) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }

    if (data.length > 0) {
      const category = data[0]._type;
      // CORRECTED: Also add pagination to category products query
      const categoryProducts = await client.fetch(
          `*[_type == $category && currency == $currency][0...20]`,
          { category, currency }
        );
      
      setSlugCategory(categoryProducts.map((product) => ({
        ...product,
        currentImageIndex: 0,
        quantity: 1,
      })));
    }
  } catch (error) {
    console.error('Error fetching products:', error);
  } finally {
    setLoading(false);
  }
}, [slug]);

  useEffect(() => {
    // Reset pagination when slug changes
  setProducts([]);
  setSlugCategory([]);
  setCurrentPage(1);
  setHasMore(true);

  fetchProducts(1, false);
    
    // Listen for currency changes in localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'selectedCurrency') {
        setCurrentPage(1);
        setHasMore(true);
        fetchProducts(1, false);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [slug, fetchProducts]);

  // Load more products
const loadMoreProducts = useCallback(() => {
  if (!loading && hasMore) {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts(nextPage, true);
  }
}, [loading, hasMore, currentPage, fetchProducts]);

  // Fetch subcategories with pagination
  const handleFetchCategory = async (category) => {
    setShowCategory(false);
    try {
      const currency = getCurrency();
      const data = await client.fetch(
        `*[_type == $category && currency == $currency][0...20]`,
        { category, currency }
      );
      setSubCategories(data);
    } catch (error) {
      console.error(error);
    }
    navigate(`/category/${category}`);
  };

  const handleImageSelect = (productId, index) => {
    setSlugCategory((prev) =>
      prev.map((product) =>
        product._id === productId ? { ...product, currentImageIndex: index } : product
      )
    );
  };

  const toggleMobileDropdown = () => {
    setShowMobileDropdown(!showMobileDropdown);
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <div className="products-page-container">
      <div className="NavPan" style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '1000',
        padding: '10px 20px',
        background: 'transparent',
        borderRadius: '0 0 10px 10px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h3 style={{ color: 'white', margin: 0 }}>
          <button 
            onClick={handleBackNavigation}
            style={{ 
              color: 'black', 
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <AiOutlineArrowLeft />Back
          </button>
        </h3>
        <h3><Link 
            to={'/'} 
            style={{ color: 'black', display:'flex', fontFamily:'inherit', alignItems:'center', height: '20px', marginLeft: '1.5%', textDecoration: 'none' }}
          >
            <GoHome />Home
          </Link></h3>
        <button
          type="button"
          className="cart-icon"
          onClick={() => setShowCart(true)}
          style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'black', 
            fontSize: '1.5rem',
            position: 'relative',
            cursor: 'pointer'
          }}
        >
          <AiOutlineShopping />
          <span className="cart-item-qty">{totalQuantities}</span>
        </button>
        <div className={`modal ${showCart ? 'active' : ''}`}><Cart /></div>
        <div className={`modalTwo ${showMobileDropdown ? 'active' : ''}`}>
          <NavBar handleFetchCategory={handleFetchCategory} toggleMobileDropdown={toggleMobileDropdown} />
        </div>
      </div>

      <div className="products-content-wrapper">
        <div className="products-display-section">
          {loading ? (
            <div className="product-loading-overlay">
               <div className="loadertwo"></div>
            </div>
          ) : showCategory ? (
            isMobile ? (
              <ProductDetail products={products} />
            ) : (
              <ProductDesktop products={products} />
            )
          ) : (
            <FetchCategory subCategories={subCategories} />
          )}
        </div>

        <div className="section-divider"></div>

        <div className="slug-products-section">
          <SlugProducts 
            slugCategory={slugCategory} 
            handleImageSelect={handleImageSelect}
            loadMoreProducts={loadMoreProducts}
            hasMore={hasMore}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default Products;